/**
 * GanttTimeline.jsx — the shared Gantt "chart" (day/week header + grid + phase bands + dependency
 * connectors + bars + hover tooltip + today marker), extracted from GanttModal.jsx (2026-09-08) so
 * the real per-project Gantt and the WBS Template editor's timeline visualizer draw from the SAME
 * component. A fix here — bar styling, a new tooltip line, how critical path reads — lands on both.
 *
 * This component renders ONLY the "Timeline" column (header + content). The caller owns the label
 * column, the outer scroll container (so the label column and this stay position-synced while
 * scrolling — exactly like GanttModal's sticky label column today), and any editing chrome (footer,
 * save/cancel, locks). Geometry comes from `../../lib/ganttLayout.js` — the caller computes its OWN
 * `layout`/`msPos` from the same pure functions (for its label column) so the two never drift; this
 * component recomputes them internally from the same inputs.
 *
 * `interactive` is undefined for a read-only view (the WBS visualizer): resize handles, the link
 * grip, and the wrench menu simply don't render, and bars aren't draggable. Pass it (see the shape
 * below) to get full parity with the original GanttModal behavior — GanttModal keeps every one of its
 * existing handlers/state; it just calls them via these props instead of local JSX.
 *
 * interactive shape (all optional):
 *   { onBarPointerDown(e,row,mode), onGripPointerDown(e,row), onWrenchClick(e,row), onDepClick(dep),
 *     onCreatePointerDown(e,row), isDragLocked(row), dragTip, linking, creating }
 */

import { forwardRef, useState, useEffect } from 'react'
import {
  parseDate, dayDiff,
} from '../../lib/cascade.js'
import {
  todayUTC, fmtShort, isLocked, computeCriticalIds, computeNetworkCriticalIds, isCompressed, calendarSpanForWork, workdaysInWindow, buildWindow, buildDayCells, buildPhaseLayout,
  elbowMidpoint, computeDepGeometry,
} from '../../lib/ganttLayout.js'

const EMPTY_SET = new Set()

// Day boxes a little narrower than a row is tall (Matt, 2026-10-29: more of the schedule on one screen).
export const ZOOMS = {
  days:  { label: 'Days',  px: 22 },
  weeks: { label: 'Weeks', px: 11 },
}

// Shared palette — import these into a label column so both sides of a Gantt-shaped view read as
// one system (critical-path red, posted green, etc. mean the same thing everywhere).
export const PRIMARY     = '#07272D'
export const TEXT        = 'var(--aj-text)'
export const BG          = 'var(--aj-bg-subtle)'
export const CRIT        = '#c0392b'
export const PROG        = '#1FB57A'
export const WAIT        = '#c9821a'   // wait rows (Client/Agency Review) — distinct from touch-time blue
export const ACCENT_BLUE = '#1a6fa8'
export const KEY_DATE        = '#8a5cc2'   // Key Dates diamond (docs/KEY_DATES.md) — distinct from bar/critical/wait colors
export const KEY_DATE_BORDER = '#6c3fa8'
export const HOVER_BG    = 'var(--aj-bg-muted)'
export const ROW_HILITE  = 'rgba(26,111,168,0.07)'
// The Build Ready colour: the column, its label and the buffer bar.
const BR_COLOR = '#1D9E75'
/** The date `n` working days (Mon-Fri) before `ymd`. */
function workdaysBefore(ymd, n) {
  const d = new Date(String(ymd).slice(0, 10) + 'T12:00:00Z')
  let left = Math.round(n)
  while (left > 0) { d.setUTCDate(d.getUTCDate() - 1); const w = d.getUTCDay(); if (w !== 0 && w !== 6) left-- }
  return d.toISOString().slice(0, 10)
}

const GanttTimeline = forwardRef(function GanttTimeline({
  rows, deps, keyDates,
  // Build Ready (docs/ALDERFLOW.md §5c2): { target, projected, bufferDays, margin, late } — the target as a
  // coloured column with its label, and the project buffer as the last row, shrinking as it is used.
  buildReady = null,
  phaseKeyFor, phaseLabelFor, orderedPhaseKeys, collapsedKeys,
  zoom, rowH = 30,
  hoveredRowId, onHoverRow,
  selectedId,
  interactive,
  readOnly: readOnlyProp = false,
  headH = 42,
  // Critical path (zero float) is a meaningful signal only when the schedule has real slack to lack —
  // a simulated WBS schedule (lib/simulateWbsSchedule.js) places every row back-to-back with no
  // separate "latest allowable" window at all, so EVERY row comes out critical by construction and
  // the red styling stops meaning anything (and drowns out the wait/amber color, which matters more
  // there). GanttModal (a real, dated schedule with genuine float) leaves this true.
  showCritical = true,
}, contentRef) {
  const px = ZOOMS[zoom].px
  // A bar sits this far inside its row: 16px bars at the 26px row (they were 16 at 30).
  const barPad = Math.round((rowH - 16) / 2)
  // Non-interactive if the caller passed no handlers at all (the WBS visualizer) OR explicitly
  // marked the view read-only (GanttModal's own record-lock state — a different concept from "was
  // an interactive prop given").
  const readOnly = readOnlyProp || !interactive
  const [hover, setHover] = useState(null)          // { id, x, y } — floating tooltip
  const [hoverDepKey, setHoverDepKey] = useState(null)

  // A drag or connector-link starting elsewhere (GanttModal's own handlers) should clear these
  // immediately, matching the original inline behavior — otherwise a stale tooltip/dep-highlight can
  // flash for one frame before the next pointermove would have cleared it anyway.
  const dragStarting = interactive && (interactive.dragTip || interactive.linking)
  useEffect(() => { if (dragStarting) { setHover(null); setHoverDepKey(null) } }, [dragStarting])

  const today = todayUTC()
  // A key date can fall outside the milestones' own date span (a custom flag set well ahead of
  // any scheduled work) — feed its date into the window as a pseudo-row (earliest_start/
  // latest_finish only) purely so the window/xOf widen to include it. buildPhaseLayout below
  // still gets the real `rows` only, so this never affects row grouping/positions.
  const datedKeyDates = (keyDates || []).filter(k => k && k.date)
  const br = buildReady && buildReady.target ? buildReady : null
  const brStart = br ? workdaysBefore(br.target, br.bufferDays || 0) : null
  const windowRows = [...rows,
    ...datedKeyDates.map(k => ({ earliest_start: k.date, latest_finish: k.date })),
    ...(br ? [{ earliest_start: brStart, latest_finish: br.target }, ...(br.projected ? [{ earliest_start: br.projected, latest_finish: br.projected }] : [])] : [])]
  const { windowStart, windowEnd, totalDays, timelineW, xOf, anchorX: todayX } = buildWindow(windowRows, px, today)
  const { dayCells, monthSegs, weekSegs, weekCells } = buildDayCells(windowStart, totalDays, px)
  const { layout, msPos, bodyH: rowsH } = buildPhaseLayout({
    rows, phaseKeyFor, phaseLabelFor, orderedKeys: orderedPhaseKeys, collapsedKeys, rowH, xOf, px,
  })
  // Critical path (2026-09-24, Matt): where the rows carry touch time (the real Gantt), it is the
  // chain whose slip would move the project's finish — a red OUTLINE and red arrows. Rows without
  // touch time (the WBS visualizer) keep the old per-bar test.
  // The buffer row sits under the last milestone (GanttModal's label column adds the matching label).
  const bodyH = rowsH + (br ? rowH : 0)
  const hasTouch = rows.some(r => r.touchDays !== undefined)
  const criticalIds = !showCritical ? EMPTY_SET : hasTouch ? computeNetworkCriticalIds(rows, deps) : computeCriticalIds(rows)

  // Day index span of a hovered work band (days scale) → header day-cell highlight.
  const hoverSpan = (() => {
    if (zoom !== 'days' || !hover) return null
    const r = rows.find(x => x.id === hover.id); if (!r) return null
    const s = parseDate(r.earliest_start || r.latest_finish), e = parseDate(r.latest_finish || r.earliest_start)
    if (!s || !e) return null
    const a = dayDiff(windowStart, s), b = dayDiff(windowStart, e)
    return [a, Math.max(a, b - 1)]
  })()

  return (
    <div style={{ position: 'relative', width: timelineW }}>
      {/* header */}
      {/* Sticky (Matt, 2026-10-27): the two date rows stay in view while the rows scroll under them. */}
      <div style={{ height: headH, borderBottom: '0.5px solid var(--aj-border)', position: 'sticky', top: 0, zIndex: 2, background: 'var(--aj-bg-elevated)' }}>
        {br && (
          <span title={`Build Ready target ${fmtShort(br.target)}`} style={{ position: 'absolute', left: xOf(br.target) + (zoom === 'days' ? px : 2), bottom: 2, transform: 'translateX(-100%)', zIndex: 1, padding: '1px 6px', borderRadius: 3, background: BR_COLOR, color: '#fff', fontFamily: 'Montserrat,sans-serif', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', pointerEvents: 'auto' }}>
            Build Ready · {fmtShort(br.target)}
          </span>
        )}
        <div style={{ position: 'absolute', top: 0, left: 0, width: timelineW, height: 22, borderBottom: '0.5px solid var(--aj-border)' }}>
          {monthSegs.map((m, i) => (
            <span key={'mo' + i} style={{ position: 'absolute', left: m.x + 4, top: 5, fontFamily: 'Montserrat,sans-serif', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', whiteSpace: 'nowrap' }}>{m.label}</span>
          ))}
          {zoom === 'days' && weekSegs.map((w, i) => (
            <span key={'wk' + i} style={{ position: 'absolute', left: w.x + 3, top: 6, fontFamily: '"DM Mono",monospace', fontSize: 8, color: 'var(--aj-text-muted)' }}>{w.wk}</span>
          ))}
        </div>
        <div style={{ position: 'absolute', top: 22, left: 0, width: timelineW, height: headH - 22 }}>
          {zoom === 'days'
            ? dayCells.map(c => (
                <div key={'d' + c.i} style={{ position: 'absolute', left: c.x, top: 0, width: px, height: headH - 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: c.weekend ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
                  borderLeft: '0.5px solid var(--aj-border)',
                  background: (hoverSpan && c.i >= hoverSpan[0] && c.i <= hoverSpan[1]) ? 'rgba(26,111,168,0.18)' : (c.weekend ? 'var(--aj-bg-muted)' : 'transparent') }}>{c.dayNum}</div>
              ))
            : weekCells.map((c, i) => (
                <div key={'wc' + i} style={{ position: 'absolute', left: c.x, top: 0, width: c.w, height: headH - 22, display: 'flex', alignItems: 'center', paddingLeft: 4, boxSizing: 'border-box',
                  fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-mid)', borderLeft: '0.5px solid var(--aj-border)' }}>{c.label}</div>
              ))}
        </div>
      </div>

      {/* content area (rows + overlays) */}
      <div ref={contentRef} style={{ position: 'relative', height: bodyH }}>
        {zoom === 'days' && dayCells.filter(c => c.weekend).map(c => (
          <div key={'we' + c.i} style={{ position: 'absolute', left: c.x, top: 0, width: px, height: bodyH, background: 'var(--aj-bg-muted)', pointerEvents: 'none' }} />
        ))}
        {(zoom === 'days' ? dayCells : weekSegs).map((c, i) => {
          const strong = zoom === 'days' ? (c.monthStart || c.weekStart) : true
          return <div key={'g' + i} style={{ position: 'absolute', left: c.x, top: 0, width: 1, height: bodyH, background: strong ? 'var(--aj-border-mid)' : 'var(--aj-border)', pointerEvents: 'none' }} />
        })}
        {layout.map((node, i) => {
          if (node.kind !== 'phase') return null
          const left = node.gs ? xOf(node.gs) : 0
          const right = node.ge ? xOf(node.ge) : 0
          const w = Math.max(right - left, 0)
          return (
            <div key={'p' + node.pid + i} style={{ position: 'absolute', left: 0, top: node.top, width: timelineW, height: rowH, background: 'var(--aj-bg-muted)', borderBottom: '0.5px solid var(--aj-border)' }}>
              {w > 0 && <div style={{ position: 'absolute', left, top: rowH / 2 - 2, width: w, height: 4, background: 'var(--aj-text-muted)', borderRadius: 2 }} />}
            </div>
          )
        })}

        <svg width={timelineW} height={bodyH} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}>
          <defs>
            <marker id="aj-gantt-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" style={{ fill: 'var(--aj-text-mid)' }} />
            </marker>
          </defs>
          {deps.map((d, i) => {
            const g = computeDepGeometry(d, msPos, px, criticalIds)
            if (!g) return null
            return <path key={i} d={g.path} fill="none" style={{ stroke: g.crit ? CRIT : 'var(--aj-text-mid)' }} strokeWidth={g.crit ? 1.4 : 1} strokeOpacity={g.crit ? 0.75 : 1} markerEnd="url(#aj-gantt-arrow)" />
          })}
        </svg>

        {/* rows */}
        {layout.map((node, i) => {
          if (node.kind !== 'ms') return null
          const r = node.row
          const est = r.earliest_start, lft = r.latest_finish
          const rowHovered = hoveredRowId === r.id
          const rowHover = { onMouseEnter: () => onHoverRow && onHoverRow(r.id), onMouseLeave: () => onHoverRow && onHoverRow(h => h === r.id ? null : h) }
          const dragLocked = interactive && interactive.isDragLocked ? interactive.isDragLocked(r) : false

          if (!est && !lft) {
            const isCreating = interactive && interactive.creating && interactive.creating.rowId === r.id
            const previewLeft  = isCreating ? Math.min(interactive.creating.x1, interactive.creating.x2) : 0
            const previewWidth = isCreating ? Math.max(Math.abs(interactive.creating.x2 - interactive.creating.x1), px) : 0
            const canCreate = !readOnly && !dragLocked && interactive.onCreatePointerDown
            return (
              <div key={r.id} {...rowHover}
                onPointerDown={canCreate ? e => interactive.onCreatePointerDown(e, r) : undefined}
                style={{ position: 'absolute', left: 0, top: node.top, width: timelineW, height: rowH, borderBottom: '0.5px solid var(--aj-border)', background: rowHovered ? ROW_HILITE : 'transparent', cursor: canCreate ? 'crosshair' : 'default' }}>
                {isCreating && (
                  <div style={{ position: 'absolute', left: previewLeft, top: barPad, width: previewWidth, height: rowH - 2 * barPad, background: 'rgba(26,111,168,0.18)', border: '1px dashed rgba(26,111,168,0.5)', borderRadius: 4, pointerEvents: 'none' }} />
                )}
                {rowHovered && canCreate && !isCreating && (
                  <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', pointerEvents: 'none', whiteSpace: 'nowrap' }}>drag to set dates</span>
                )}
              </div>
            )
          }

          const pos = msPos.get(r.id)
          const left = pos.left, right = pos.right
          const barW = right - left
          // The dark fill is the WORK (touch time) where the row carries it; a feeder / wait bar is
          // all waiting, so it stays full. Rows without touch time (the WBS visualizer) keep the old fill.
          const hasOwnTouch = r.touchDays !== undefined && !r.isWait
          const workCal = hasOwnTouch ? calendarSpanForWork(est, r.touchDays || 0) : (r.duration_days || 0)
          const durW = Math.min(workCal * px, barW)
          const compressed = hasOwnTouch && isCompressed(r)
          const locked = isLocked(r)
          const float = (parseDate(lft) && parseDate(est)) ? Math.max(dayDiff(parseDate(est), parseDate(lft)) - (r.duration_days || 0), 0) : 0
          const critical = criticalIds.has(r.id)
          const selected = selectedId === r.id
          const pct = Math.max(0, Math.min(1, r.progress || 0))
          const postedOn = r.posted || null
          const isWait = !!r.isWait
          // A feeder (work owed to us, build step 5) is always amber — its pin is the recorded
          // expected date, not a hand lock — and red once late. Not yet sent = our typical time,
          // drawn with a dashed edge so a guess never reads like a date someone gave us.
          const fd = !postedOn && r.feeder ? r.feeder : null
          const fLate = fd && fd.state === 'late'
          // Color: posted → solid green, late feeder → red, feeder → amber, locked → gray, critical → red, wait → amber, else blue.
          // Critical path = a red OUTLINE only (the fill keeps saying what kind of bar it is). A bar
          // squeezed shorter than its touch time fills a deep red — "not possible in the time given".
          // Amber stays feeders; green posted; gray pinned. (Matt, 2026-09-24.)
          const onPath = hasTouch && critical
          const barBg     = postedOn ? 'rgba(31,181,122,0.22)' : fLate ? 'rgba(192,57,43,0.22)' : compressed ? 'rgba(142,28,28,0.28)' : fd ? 'rgba(201,130,26,0.18)' : locked ? 'var(--aj-bg-muted)'    : (!hasTouch && critical) ? 'rgba(192,57,43,0.16)' : isWait ? 'rgba(201,130,26,0.18)' : 'rgba(26,111,168,0.18)'
          const barBorder = postedOn ? PROG                    : (fLate || onPath) ? CRIT       : compressed ? 'rgba(142,28,28,0.9)'  : fd ? 'rgba(201,130,26,0.7)'  : locked ? 'var(--aj-text-muted)'  : critical ? 'rgba(192,57,43,0.6)'  : isWait ? 'rgba(201,130,26,0.6)'  : 'rgba(26,111,168,0.55)'
          const durBg     = postedOn ? 'rgba(31,181,122,0.45)' : fLate ? 'rgba(192,57,43,0.45)' : compressed ? 'rgba(142,28,28,0.75)' : fd ? 'rgba(201,130,26,0.45)' : locked ? 'var(--aj-text-muted)'   : (!hasTouch && critical) ? 'rgba(192,57,43,0.5)'  : isWait ? 'rgba(201,130,26,0.55)' : 'rgba(26,111,168,0.55)'
          const borderStyle = fd && fd.state === 'typical' ? 'dashed' : 'solid'

          return (
            <div key={r.id} {...rowHover} style={{ position: 'absolute', left: 0, top: node.top, width: timelineW, height: rowH, borderBottom: '0.5px solid var(--aj-border)', background: rowHovered ? ROW_HILITE : 'transparent' }}>
              {rowHovered && !readOnly && interactive.onGripPointerDown && (
                <div title="Drag to a predecessor to make it block this milestone"
                  onPointerDown={e => interactive.onGripPointerDown(e, r)}
                  style={{ position: 'absolute', left: Math.max(2, left - 12), top: '50%', transform: 'translateY(-50%)', width: 9, height: 9, borderRadius: '50%', background: '#e0a020', border: '1px solid var(--aj-bg-elevated)', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.25)', cursor: 'crosshair', zIndex: 2 }} />
              )}
              <div onPointerDown={!readOnly && interactive.onBarPointerDown ? e => interactive.onBarPointerDown(e, r, 'move') : undefined}
                onPointerMove={e => { if (!interactive || (!interactive.dragTip && !interactive.linking)) setHover({ id: r.id, x: e.clientX, y: e.clientY }) }}
                onPointerLeave={() => { if (!interactive || !interactive.dragTip) setHover(h => (h && h.id === r.id) ? null : h) }}
                style={{ position: 'absolute', left, top: barPad, width: barW, height: rowH - 2 * barPad, background: barBg, border: ((fd || onPath) ? '1.5px ' : '0.5px ') + borderStyle + ' ' + barBorder, borderRadius: 4, cursor: (readOnly || dragLocked) ? 'default' : 'grab', boxShadow: selected ? '0 0 0 2px var(--aj-text)' : 'none' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: durW, background: durBg, borderRadius: '4px 0 0 4px', pointerEvents: 'none' }} />
                {postedOn && (
                  <span style={{ position: 'absolute', left: 6, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 4, fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#0f6b48', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: Math.max(barW - 10, 0), pointerEvents: 'none' }}>
                    <span style={{ fontWeight: 700 }}>✓</span>{barW > 90 && <span>posted {fmtShort(postedOn)}</span>}
                  </span>
                )}
                {!postedOn && pct > 0 && <div style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: Math.max(barW * pct, 2), background: PROG, borderRadius: '0 0 0 4px', pointerEvents: 'none' }} />}
                {!readOnly && !dragLocked && interactive.onBarPointerDown && <>
                  <div onPointerDown={e => interactive.onBarPointerDown(e, r, 'start')} style={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' }} />
                  <div onPointerDown={e => interactive.onBarPointerDown(e, r, 'end')} style={{ position: 'absolute', right: -3, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' }} />
                </>}
              </div>
              {rowHovered && !readOnly && interactive.onWrenchClick && (
                <button title="Actions — lock dates, edit dependencies"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); interactive.onWrenchClick(e, r) }}
                  style={{ position: 'absolute', left: left + barW + 3, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, lineHeight: '13px', textAlign: 'center', fontSize: 10, padding: 0, borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', cursor: 'pointer', zIndex: 2 }}>🔧</button>
              )}
            </div>
          )
        })}

        <svg width={timelineW} height={bodyH} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}>
          <defs>
            <marker id="aj-gantt-arrow-hover" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" style={{ fill: ACCENT_BLUE }} />
            </marker>
          </defs>
          {deps.map((d, i) => {
            const g = computeDepGeometry(d, msPos, px, criticalIds)
            if (!g) return null
            const depHovered = !readOnly && hoverDepKey === g.depKey
            const mid = elbowMidpoint(g.x1, g.y1, g.xEnd, g.y2)
            return (
              <g key={i}>
                {!readOnly && interactive.onDepClick && (
                  <path d={g.path} fill="none" stroke="transparent" strokeWidth={14}
                    style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                    onMouseEnter={() => { if (!interactive.dragTip && !interactive.linking) setHoverDepKey(g.depKey) }}
                    onMouseLeave={() => setHoverDepKey(h => h === g.depKey ? null : h)}
                    onClick={() => interactive.onDepClick(d)} />
                )}
                {depHovered && <>
                  <path d={g.path} fill="none" style={{ pointerEvents: 'none', stroke: ACCENT_BLUE }}
                    strokeWidth={g.crit ? 2 : 1.8} markerEnd="url(#aj-gantt-arrow-hover)" />
                  <g transform={`translate(${mid.x},${mid.y})`} style={{ pointerEvents: 'none' }}>
                    <circle r={7.5} fill="var(--aj-bg-elevated)" stroke={ACCENT_BLUE} strokeWidth={1} />
                    <line x1={-3} y1={-3} x2={3} y2={3} stroke={CRIT} strokeWidth={1.6} strokeLinecap="round" />
                    <line x1={-3} y1={3} x2={3} y2={-3} stroke={CRIT} strokeWidth={1.6} strokeLinecap="round" />
                  </g>
                </>}
              </g>
            )
          })}
          {interactive && interactive.linking && (
            <path d={`M ${interactive.linking.x1} ${interactive.linking.y1} C ${interactive.linking.x1 + 36} ${interactive.linking.y1}, ${interactive.linking.x2 - 36} ${interactive.linking.y2}, ${interactive.linking.x2} ${interactive.linking.y2}`}
              fill="none" stroke="#e0a020" strokeWidth="2" strokeDasharray="4 3" />
          )}
        </svg>

        {br && (() => {
          // The Build Ready target: a coloured column down the whole chart.
          const x = xOf(br.target), w = zoom === 'days' ? px : 3
          // The buffer: a milestone-like bar leading up to the target, its full length faint, what is
          // left solid. Left = the target less the margin still in hand; late = a red bar past the target.
          const full0 = xOf(brStart)
          const leftDate = br.late ? null : (br.margin >= (br.bufferDays || 0) ? brStart : workdaysBefore(br.target, Math.max(0, br.margin)))
          const left = leftDate ? xOf(leftDate) : x
          const used = Math.max(0, Math.min(br.bufferDays || 0, (br.bufferDays || 0) - Math.max(0, br.margin)))
          const lateRight = br.late && br.projected ? xOf(br.projected) : null
          const top = rowsH, barTop = top + barPad, barH = rowH - 2 * barPad
          const label = br.late ? `Late by ${-br.margin} working days` : used <= 0 ? `Buffer · ${br.bufferDays} working days, none used` : `Buffer · ${Math.max(0, (br.bufferDays || 0) - used)} of ${br.bufferDays} working days left`
          return (
            <>
              <div style={{ position: 'absolute', left: x, top: 0, width: w, height: bodyH, background: BR_COLOR, opacity: zoom === 'days' ? 0.22 : 0.85, pointerEvents: 'none', zIndex: 1 }} />
              <div style={{ position: 'absolute', left: 0, top, width: timelineW, height: rowH, borderBottom: '0.5px solid var(--aj-border)', background: 'rgba(29,158,117,0.05)' }} />
              <div title={`The project buffer: ${br.bufferDays} working days before Build Ready. Projected finish ${fmtShort(br.projected)}.`}
                style={{ position: 'absolute', left: full0, top: barTop, width: Math.max(x - full0, 2), height: barH, borderRadius: 4, border: '1px dashed ' + BR_COLOR, background: 'rgba(192,57,43,0.10)', boxSizing: 'border-box' }} />
              {!br.late && (
                <div style={{ position: 'absolute', left, top: barTop, width: Math.max(x - left, 2), height: barH, borderRadius: 4, background: BR_COLOR, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <span style={{ paddingLeft: 6, fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#fff', whiteSpace: 'nowrap' }}>{label}</span>
                </div>
              )}
              {br.late && lateRight != null && (
                <div style={{ position: 'absolute', left: x, top: barTop, width: Math.max(lateRight - x, 2), height: barH, borderRadius: 4, background: CRIT, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <span style={{ paddingLeft: 6, fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#fff', whiteSpace: 'nowrap' }}>{label}</span>
                </div>
              )}
            </>
          )
        })()}

        {todayX != null && (zoom === 'days'
          ? <div style={{ position: 'absolute', left: todayX, top: 0, width: px, height: bodyH, background: 'rgba(192,57,43,0.08)', pointerEvents: 'none' }} />
          : <div style={{ position: 'absolute', left: todayX, top: 0, width: 1.5, height: bodyH, background: CRIT, opacity: 0.7, pointerEvents: 'none' }} />
        )}

        {/* Key Dates (docs/KEY_DATES.md) — calendar flags, not milestones. One diamond per dated
            key date, at its own x-position, with a thin guide line down the body. */}
        {datedKeyDates.map(kd => {
          const x = xOf(kd.date)
          return (
            <div key={'kd-' + kd.id} style={{ position: 'absolute', left: x, top: 0, pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', left: -0.75, top: 0, width: 1.5, height: bodyH, background: KEY_DATE, opacity: 0.35 }} />
              <div
                title={kd.label + ' — ' + fmtShort(kd.date)}
                style={{ position: 'absolute', left: -5, top: -5, width: 10, height: 10, background: KEY_DATE, border: '1px solid ' + KEY_DATE_BORDER, transform: 'rotate(45deg)', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', pointerEvents: 'auto', cursor: 'default' }}
              />
            </div>
          )
        })}
      </div>

      {/* Floating hover tooltip */}
      {hover && (() => {
        const r = rows.find(x => x.id === hover.id)
        if (!r) return null
        const tipTouch = r.touchDays !== undefined && !r.isWait
        const s = tipTouch
          ? ((r.earliest_start && r.latest_finish) ? workdaysInWindow(r.earliest_start, r.latest_finish) - (r.touchDays || 0) : null)
          : (() => { const e = parseDate(r.earliest_start), l = parseDate(r.latest_finish); return (e && l) ? dayDiff(e, l) - (r.duration_days || 0) : null })()
        const squeezed = tipTouch && isCompressed(r)
        const fmtD = (n) => (Math.round(n * 10) / 10)
        const crit = criticalIds.has(hover.id)
        const pname = phaseLabelFor(phaseKeyFor(r))
        const MUT = 'var(--aj-text-mid)'
        const left = Math.min(hover.x + 14, window.innerWidth - 270)
        const top = Math.min(hover.y + 14, window.innerHeight - 170)
        return (
          <div style={{ position: 'fixed', left, top, zIndex: 720, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, boxShadow: '0 6px 28px rgba(0,0,0,0.20)', padding: '9px 11px', width: 246, pointerEvents: 'none', fontFamily: '"DM Sans",sans-serif' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: TEXT, lineHeight: 1.3, marginBottom: 2 }}>{r.name}</div>
            <div style={{ fontSize: 10.5, color: MUT, marginBottom: 6 }}>{pname}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: '"DM Mono",monospace', fontSize: 11, color: TEXT }}>
              <span>{r.earliest_start || '—'}</span><span style={{ color: MUT }}>→</span><span>{r.latest_finish || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 12, fontFamily: '"DM Mono",monospace', fontSize: 11, color: MUT, marginTop: 4 }}>
              <span>{tipTouch ? fmtD(r.touchDays || 0) + 'd work' : (r.duration_days || 0) + 'd ' + (r.isWait ? 'wait' : 'work')}</span>
              <span>{r.fc || s === null ? '' : (s < 0 ? fmtD(Math.abs(s)) + 'd over' : fmtD(s) + 'd float')}</span>
            </div>
            {r.fc && !r.posted && (() => {
              // THE FORECAST, IN WORDS (Matt, 2026-10-27): what the bar is made of and whether running late here
              // costs Build Ready anything, said so a person needs no other context.
              const f = r.fc
              const lines = []
              if (f.kind === 'ours' && f.work != null) lines.push(f.captain == null && f.pace && f.pace > 1.05
                ? `${fmtD(f.work)} days of work, planned at our usual pace (${fmtD(f.pace)}x), so the bar is about ${fmtD(f.work * f.pace)} working days.`
                : `${fmtD(f.work)} days of work.`)
              else if (f.wait != null) lines.push(`A wait on someone else: about ${fmtD(f.wait)} working days, our typical time.`)
              if (f.captain != null) lines.push(`The captain's report says ${fmtD(f.captain)} working day${f.captain === 1 ? '' : 's'} left. That sets the end.`)
              lines.push(f.slack > 0
                ? `Not on the path to Build Ready: it can run ${f.slack} working day${f.slack === 1 ? '' : 's'} late before it uses any buffer.`
                : 'On the path to Build Ready: every day it runs late uses a day of the buffer.')
              return <div style={{ fontSize: 11, color: TEXT, marginTop: 6, lineHeight: 1.45 }}>{lines.map((l, i) => <div key={i} style={{ marginTop: i ? 3 : 0, color: i === lines.length - 1 && !f.slack ? CRIT : TEXT }}>{l}</div>)}</div>
            })()}
            {!r.fc && !tipTouch && r.typicalDays != null && Math.abs((r.duration_days || 0) - r.typicalDays) >= 1 && (
              <div title={r.typicalLabel || ''} style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: MUT, marginTop: 3, opacity: 0.85 }}>
                ≈{r.typicalDays}d typical for this type
              </div>
            )}
            {r.feeder && !r.posted && (() => {
              const f = r.feeder
              const party = { agency: 'the agency', client: 'the client', consultant: 'the consultant', gc: 'the GC' }[f.owedBy] || 'them'
              const line = f.state === 'late' ? `Late ${f.daysLate} day${f.daysLate === 1 ? '' : 's'}: was expected ${fmtShort(f.expectedOn)}. Stretches to today until there's a new guess.`
                : f.state === 'out' ? `Out with ${party} since ${fmtShort(f.askedOn)}. Expected ${fmtShort(f.expectedOn)}, ${f.expectedSource === 'them' ? 'their date' : 'our estimate'}.`
                : f.state === 'back' ? `Came back ${fmtShort(f.arrivedOn)}.`
                : `Not sent yet. Our typical time for ${party}${f.typicalWorkdays ? ' (' + f.typicalWorkdays + ' days)' : ''}.`
              return <div style={{ fontSize: 11, color: f.state === 'late' ? CRIT : MUT, marginTop: 5, lineHeight: 1.4 }}>{line}</div>
            })()}
            {r.assignee ? <div style={{ fontSize: 11, color: MUT, marginTop: 5 }}>{r.assignee}</div> : null}
            {!r.isWait && (() => {
              const pct = Math.max(0, Math.min(1, r.progress || 0))
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--aj-bg-muted)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: Math.round(pct * 100) + '%', height: '100%', background: PROG }} />
                  </div>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: MUT }}>{Math.round(pct * 100)}%</span>
                </div>
              )
            })()}
            {squeezed && <div style={{ marginTop: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', color: '#8e1c1c' }}>● SHORTER THAN ITS WORK · {fmtD(r.touchDays)}d of work in {workdaysInWindow(r.earliest_start, r.latest_finish)}d</div>}
            {crit && <div style={{ marginTop: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', color: CRIT }}>● CRITICAL PATH{tipTouch ? ' · A SLIP HERE MOVES THE FINISH' : ' · NO FLOAT'}</div>}
          </div>
        )
      })()}

      {/* Live date popover while dragging/resizing a bar (GanttModal only — pass via interactive.dragTip) */}
      {interactive && interactive.dragTip && (() => {
        const dragTip = interactive.dragTip
        const left = Math.min(Math.max(dragTip.x - 60, 8), window.innerWidth - 160)
        const top = Math.max(8, dragTip.y - 46)
        const days = (() => { const e = parseDate(dragTip.est), l = parseDate(dragTip.lft); return (e && l) ? dayDiff(e, l) : null })()
        return (
          <div style={{ position: 'fixed', left, top, zIndex: 725, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, boxShadow: '0 4px 18px rgba(0,0,0,0.25)', padding: '6px 10px', pointerEvents: 'none', fontFamily: '"DM Mono",monospace', fontSize: 12, color: TEXT, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: dragTip.mode !== 'end' ? 700 : 400, opacity: dragTip.mode === 'end' ? 0.55 : 1 }}>{fmtShort(dragTip.est)}</span>
            <span style={{ color: 'var(--aj-text-mid)' }}>→</span>
            <span style={{ fontWeight: dragTip.mode !== 'start' ? 700 : 400, opacity: dragTip.mode === 'start' ? 0.55 : 1 }}>{fmtShort(dragTip.lft)}</span>
            {days != null && <span style={{ color: 'var(--aj-text-mid)', fontSize: 10.5 }}>({days}d)</span>}
          </div>
        )
      })()}
    </div>
  )
})

export default GanttTimeline
